import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Form,
  Alert,
  Spinner,
  ProgressBar,
  Nav,
  Tab
} from 'react-bootstrap';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  FaDollarSign,
  FaChartLine,
  FaBolt,
  FaExclamationTriangle,
  FaDownload,
  FaCog
} from 'react-icons/fa';

// API base URL configuration - hardcoded to fix double API issue
const API_BASE_URL = 'http://localhost:8080/api';

interface TokenUsage {
  totalRequests: number;
  tokens: {
    input: number;
    output: number;
    total: number;
  };
  costs: {
    input: number;
    output: number;
    total: number;
  };
  cacheHitRate: number;
  avgResponseTime: number;
  serviceBreakdown: Array<{
    service_type: string;
    _sum: {
      total_tokens: number;
      total_cost: number;
    };
    _count: {
      id: number;
    };
  }>;
}

interface Analytics {
  dailyTrend: Array<{
    date: string;
    tokens: number;
    cost: number;
    requests: number;
  }>;
  topUsers: Array<{
    lead_id?: string;
    customer_id?: string;
    _sum: {
      total_tokens: number;
      total_cost: number;
    };
  }>;
  totalUsage: {
    _sum: {
      total_tokens: number;
      total_cost: number;
    };
    _count: {
      id: number;
    };
  };
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const UsageAnalytics: React.FC = () => {
  const [usage, setUsage] = useState<TokenUsage | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState('overview');

  useEffect(() => {
    console.log('[UsageAnalytics] Component mounted, period:', period);
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Debug logging
      const token = localStorage.getItem('auth_token');
      const fullUrl = `${API_BASE_URL}/unified-chat/usage/summary?period=${period}`;
      console.log('[UsageAnalytics] Token:', token ? 'Present' : 'Missing');
      console.log('[UsageAnalytics] API_BASE_URL:', API_BASE_URL);
      console.log('[UsageAnalytics] Full URL:', fullUrl);
      console.log('[UsageAnalytics] Expected: http://localhost:8080/api/unified-chat/usage/summary?period=' + period);

      // Fetch usage summary
      const summaryRes = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      console.log('[UsageAnalytics] Response status:', summaryRes.status);
      
      if (!summaryRes.ok) {
        const errorText = await summaryRes.text();
        console.error('[UsageAnalytics] Error response:', errorText);
        throw new Error(`Failed to fetch usage summary: ${summaryRes.status} ${summaryRes.statusText}`);
      }

      const summaryData = await summaryRes.json();
      console.log('[UsageAnalytics] Summary data received:', summaryData);
      setUsage(summaryData.usage);

      // Fetch analytics (admin only)
      const userRole = localStorage.getItem('userRole');
      if (userRole === 'admin' || userRole === 'broker') {
        const analyticsRes = await fetch(`${API_BASE_URL}/unified-chat/usage/analytics`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          }
        });

        if (analyticsRes.ok) {
          const analyticsData = await analyticsRes.json();
          setAnalytics(analyticsData.analytics);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => `$${(value || 0).toFixed(2)}`;
  const formatNumber = (value: number) => (value || 0).toLocaleString();

  const exportReport = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      window.location.href = `${API_BASE_URL}/unified-chat/usage/export?format=csv&token=${token}`;
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Usage Data</Alert.Heading>
        <p>{error}</p>
        <Button onClick={fetchData} variant="outline-danger">Try Again</Button>
      </Alert>
    );
  }

  return (
    <>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h4>Token Usage Analytics</h4>
          <p className="text-muted">Monitor API usage, costs, and performance metrics</p>
        </Col>
        <Col xs="auto">
          <Button variant="outline-primary" size="sm" onClick={exportReport}>
            <FaDownload className="me-2" />
            Export Report
          </Button>
        </Col>
      </Row>

      {/* Period Selector */}
      <div className="mb-4">
        <Button
          variant={period === 'day' ? 'primary' : 'outline-secondary'}
          size="sm"
          className="me-2"
          onClick={() => setPeriod('day')}
        >
          Day
        </Button>
        <Button
          variant={period === 'week' ? 'primary' : 'outline-secondary'}
          size="sm"
          className="me-2"
          onClick={() => setPeriod('week')}
        >
          Week
        </Button>
        <Button
          variant={period === 'month' ? 'primary' : 'outline-secondary'}
          size="sm"
          onClick={() => setPeriod('month')}
        >
          Month
        </Button>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaChartLine className="text-primary mb-2" size={24} />
              <h6>Total Requests</h6>
              <h4>{formatNumber(usage?.totalRequests || 0)}</h4>
              <small className="text-muted">This {period}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaBolt className="text-warning mb-2" size={24} />
              <h6>Total Tokens</h6>
              <h4>{formatNumber(usage?.tokens?.total || 0)}</h4>
              <small className="text-muted">
                {formatNumber(usage?.tokens?.input || 0)} in / {formatNumber(usage?.tokens?.output || 0)} out
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaDollarSign className="text-success mb-2" size={24} />
              <h6>Total Cost</h6>
              <h4>{formatCurrency(usage?.costs?.total || 0)}</h4>
              <small className="text-muted">
                {formatCurrency(usage?.costs?.input || 0)} in / {formatCurrency(usage?.costs?.output || 0)} out
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="text-center">
            <Card.Body>
              <FaChartLine className="text-info mb-2" size={24} />
              <h6>Cache Hit Rate</h6>
              <h4>{(usage?.cacheHitRate || 0).toFixed(1)}%</h4>
              <Badge bg={usage?.cacheHitRate && usage.cacheHitRate > 70 ? 'success' : 'warning'}>
                {usage?.cacheHitRate && usage.cacheHitRate > 70 ? 'Good' : 'Needs Improvement'}
              </Badge>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Analytics Tabs */}
      <Tab.Container defaultActiveKey="overview">
        <Nav variant="pills" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="overview">Overview</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="usage-logs">Usage Logs</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="cost-config">Cost Configuration</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Overview Tab */}
          <Tab.Pane eventKey="overview">
            {/* Service Breakdown */}
            {usage?.serviceBreakdown && usage.serviceBreakdown.length > 0 && (
              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <Card>
                    <Card.Header>Usage by Service</Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={usage.serviceBreakdown}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry: any) => `${entry.service_type}: ${(entry.percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="_sum.total_tokens"
                          >
                            {usage.serviceBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: number) => formatNumber(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Daily Trend */}
                {analytics?.dailyTrend && (
                  <Col xs={12} md={6}>
                    <Card>
                      <Card.Header>Daily Usage Trend</Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={analytics.dailyTrend.slice(-7)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="tokens" stroke="#8884d8" name="Tokens" />
                            <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#82ca9d" name="Cost ($)" />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>
            )}

            {/* Top Users (Admin Only) */}
            {analytics?.topUsers && analytics.topUsers.length > 0 && (
              <Card>
                <Card.Header>Top Users by Cost</Card.Header>
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th className="text-end">Tokens</th>
                        <th className="text-end">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topUsers.slice(0, 10).map((user, idx) => (
                        <tr key={idx}>
                          <td>User {user.lead_id || user.customer_id || 'Unknown'}</td>
                          <td className="text-end">{formatNumber(user._sum.total_tokens)}</td>
                          <td className="text-end">{formatCurrency(user._sum.total_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Tab.Pane>

          {/* Usage Logs Tab */}
          <Tab.Pane eventKey="usage-logs">
            <UsageLogs />
          </Tab.Pane>

          {/* Cost Configuration Tab */}
          <Tab.Pane eventKey="cost-config">
            <CostConfiguration />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </>
  );
};

// Usage Logs Component
const UsageLogs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/unified-chat/usage/logs?limit=20&offset=${page * 20}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Header>Recent Usage Logs</Card.Header>
      <Card.Body>
        <Table responsive hover size="sm">
          <thead>
            <tr>
              <th>Time</th>
              <th>Service</th>
              <th>Endpoint</th>
              <th className="text-end">Tokens</th>
              <th className="text-end">Cost</th>
              <th className="text-center">Cache</th>
              <th className="text-end">Response</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log: any) => (
              <tr key={log.id}>
                <td>{new Date(log.created_at).toLocaleString()}</td>
                <td>
                  <Badge bg={
                    log.service_type === 'mls_search' ? 'primary' :
                    log.service_type === 'ai_chat' ? 'success' : 'secondary'
                  }>
                    {log.service_type}
                  </Badge>
                </td>
                <td>{log.endpoint}</td>
                <td className="text-end">{log.total_tokens}</td>
                <td className="text-end">${log.total_cost.toFixed(4)}</td>
                <td className="text-center">{log.cache_hit ? '✓' : '-'}</td>
                <td className="text-end">{log.response_time_ms}ms</td>
              </tr>
            ))}
          </tbody>
        </Table>
        
        <div className="d-flex justify-content-between mt-3">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            Previous
          </Button>
          <span>Page {page + 1}</span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setPage(page + 1)}
            disabled={logs.length < 20}
          >
            Next
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

// Cost Configuration Component
const CostConfiguration: React.FC = () => {
  const [costConfig, setCostConfig] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCostConfig();
  }, []);

  const fetchCostConfig = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/unified-chat/cost-config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCostConfig(data.costConfig || {});
      }
    } catch (error) {
      console.error('Failed to fetch cost config:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <span>Cost Configuration</span>
          <FaCog />
        </div>
      </Card.Header>
      <Card.Body>
        <Table responsive>
          <thead>
            <tr>
              <th>Service</th>
              <th className="text-end">Input Cost/1K tokens</th>
              <th className="text-end">Output Cost/1K tokens</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(costConfig).map(([service, costs]: [string, any]) => (
              <tr key={service}>
                <td>{service.replace('_', ' ').toUpperCase()}</td>
                <td className="text-end">${costs.inputCostPer1k}</td>
                <td className="text-end">${costs.outputCostPer1k}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default UsageAnalytics;